<template>
  <div class="w-full" data-testid="sheet-mirror">
    <!-- Indicador discreto de assertividade da previsão (mockup 05, pin 7). -->
    <div
      v-if="coverage.total > 0"
      class="flex items-center gap-2 mb-2 text-xs text-neutral50"
      data-testid="sheet-mirror-coverage"
    >
      <span class="h-1.5 w-28 rounded-full bg-lightGray30 overflow-hidden">
        <span
          class="block h-full rounded-full bg-primary30"
          :style="{ width: `${coverage.percentage}%` }"
        />
      </span>
      <span>{{ coverageLabel }}</span>
    </div>

    <div class="border border-lightGray30 rounded-lg overflow-x-auto bg-white">
      <table class="w-full text-sm text-left border-collapse">
        <thead>
          <tr class="bg-gray-100 text-[11px] uppercase tracking-wide text-neutral50">
            <th class="py-2 px-2 w-10 font-bold">#</th>
            <!-- Pedido do tester: cruzar a linha da folha com o plantão da agenda. -->
            <th class="py-2 px-2 w-20 font-bold">Plantão</th>
            <th class="py-2 px-2 w-40 font-bold">Estado da linha</th>
            <!--
              Ações da linha ao lado do nome (pedido do PO, 2026-09-12): "Substituir médico" e
              "Não consta na folha" eram as duas decisões mais usadas e viviam escondidas no
              menu ⋯ do outro extremo da tabela. Cabeçalho sem rótulo visível — a coluna é de
              ícones — mas com nome para quem usa leitor de tela.
            -->
            <th class="py-2 px-2 w-16 font-bold" aria-label="Ações da linha" />
            <th class="py-2 px-2 font-bold">Médico</th>
            <th class="py-2 px-2 w-32 font-bold">CRM</th>
            <!--
              Pacote 2026-09-10: as colunas de conferência de assinatura/carimbo ficam ao
              lado do horário que conferem, com os MESMOS rótulos da grade clássica.
            -->
            <template v-for="field in TIME_FIELDS" :key="field.key">
              <th class="py-2 px-2 w-28 font-bold">{{ field.label }}</th>
              <th
                v-if="showSignatureStamp"
                class="py-2 px-2 w-24 font-bold text-center"
                :data-testid="`sheet-mirror-${field.checkKey}-header`"
              >
                {{ field.checkLabel }}
              </th>
            </template>
            <!--
              Cara-crachá V1 — em que página da folha esta linha está. Clicar no número pula
              a folha; o botão ⌖ marca a página que o visualizador mostra agora. Sem OCR.
            -->
            <!--
              "Folha" sozinho era ambíguo ao lado das colunas de conferência DA folha: o
              rótulo agora diz que a coluna guarda a PÁGINA. Cabe em duas linhas, como
              "Ass. e Carimbo Entrada" já faz.
            -->
            <th class="py-2 px-2 w-28 font-bold text-center">Página da Folha</th>
            <th class="py-2 px-2 w-12" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="row.entryId ?? `extra-${index}`"
            :data-testid="`sheet-mirror-row-${index}`"
            :class="[rowClass(row), selectedIndex === index ? SELECTED_ROW_CLASS : '']"
            @click="handleRowClick(index, $event)"
            @focusin="emit('selectRow', index)"
          >
            <td class="py-1.5 px-2 text-xs text-neutral50 align-middle">{{ index + 1 }}</td>

            <td
              class="py-1.5 px-2 align-middle font-mono text-xs text-neutral50"
              :data-testid="`sheet-mirror-station-id-${index}`"
            >
              {{ row.doctorStationId ?? '—' }}
            </td>

            <td class="py-1.5 px-2 align-middle">
              <span
                class="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold whitespace-nowrap"
                :class="chipClass(row)"
                :data-testid="`sheet-mirror-state-${index}`"
              >
                <span class="h-1.5 w-1.5 rounded-full" :class="chipDotClass(row)" />
                {{ rowStateChipLabel(resolveRowState(row)) }}
              </span>
            </td>

            <!--
              Ações diretas da linha, coladas ao nome do médico. Tooltip do Vuetify E `title`:
              o primeiro é o que o mouse vê, o segundo é o que teclado e leitor de tela
              alcançam — um ícone sozinho não diz o que a ação faz nem o que ela NÃO faz
              ("não é texto livre", "não assinou a folha").
            -->
            <td class="py-1.5 px-2 align-middle">
              <div class="flex items-center justify-center gap-1">
                <v-tooltip
                  v-if="row.entryId !== null"
                  :text="REPLACE_ACTION_TOOLTIP"
                  location="top"
                >
                  <template #activator="{ props: tooltipProps }">
                    <button
                      v-bind="tooltipProps"
                      type="button"
                      class="h-6 w-6 rounded flex items-center justify-center text-primary30 hover:bg-primary5 disabled:opacity-40"
                      :title="REPLACE_ACTION_TOOLTIP"
                      :aria-label="`Substituir médico da linha ${index + 1}`"
                      :disabled="disabled"
                      :data-testid="`sheet-mirror-replace-${index}`"
                      @click.stop="openReplace(index, $event)"
                    >
                      <v-icon :icon="mdiAccountSwitchOutline" size="16" />
                    </button>
                  </template>
                </v-tooltip>

                <v-tooltip
                  v-if="row.entryId !== null && !isAbsent(row)"
                  :text="MARK_ABSENT_ACTION_TOOLTIP"
                  location="top"
                >
                  <template #activator="{ props: tooltipProps }">
                    <button
                      v-bind="tooltipProps"
                      type="button"
                      class="h-6 w-6 rounded flex items-center justify-center text-negative hover:bg-negative/10 disabled:opacity-40"
                      :title="MARK_ABSENT_ACTION_TOOLTIP"
                      :aria-label="`Marcar a linha ${index + 1} como não constante na folha`"
                      :disabled="disabled"
                      :data-testid="`sheet-mirror-mark-absent-${index}`"
                      @click.stop="markAbsent(index)"
                    >
                      <v-icon :icon="mdiAccountOffOutline" size="16" />
                    </button>
                  </template>
                </v-tooltip>

                <!--
                  A ação inversa ocupa o lugar do botão vermelho: marcar ausência era terminal
                  e deixava a linha inutilizável até alguém achar o "Desfazer" no menu.
                -->
                <v-tooltip v-if="isAbsent(row)" :text="UNMARK_ABSENT_ACTION_TOOLTIP" location="top">
                  <template #activator="{ props: tooltipProps }">
                    <button
                      v-bind="tooltipProps"
                      type="button"
                      class="h-6 w-6 rounded flex items-center justify-center text-neutral50 hover:bg-primary5 disabled:opacity-40"
                      :title="UNMARK_ABSENT_ACTION_TOOLTIP"
                      :aria-label="unmarkAbsentAriaLabel(index)"
                      :disabled="disabled"
                      :data-testid="`sheet-mirror-unmark-absent-${index}`"
                      @click.stop="unmarkAbsent(index)"
                    >
                      <v-icon :icon="mdiUndoVariant" size="16" />
                    </button>
                  </template>
                </v-tooltip>
              </div>
            </td>

            <!-- Decisão 4: identidade de linha pré-preenchida NUNCA é texto livre. -->
            <td class="py-1.5 px-2 align-middle">
              <span
                v-if="row.isPrefillBorn"
                class="block max-w-[220px] truncate font-semibold"
                :title="row.doctorName || undefined"
                :data-testid="`sheet-mirror-doctor-${index}`"
                >{{ row.doctorName || '—' }}</span
              >
              <!--
                Linha extra: reaproveita o autocomplete do PRD32. Escolher uma
                sugestão preenche nome E CRM de uma vez (`selectSuggestion`).
              -->
              <DoctorSuggestionCell
                v-else-if="containerId"
                :model-value="row.doctorName"
                :container-id="containerId"
                field="name"
                column-key="rawDoctorName"
                :row-index="index"
                @update:model-value="emitCellValue(index, 'doctorName', $event)"
                @select="selectSuggestion(index, $event)"
              />
              <input
                v-else
                type="text"
                class="w-full rounded border border-lightGray30 px-2 py-1 text-sm"
                placeholder="Nome do médico"
                :value="row.doctorName"
                :data-testid="`sheet-mirror-doctor-input-${index}`"
                @input="emitCell(index, 'doctorName', $event)"
              />
            </td>

            <td class="py-1.5 px-2 align-middle">
              <span
                v-if="row.isPrefillBorn"
                class="font-mono text-xs"
                :data-testid="`sheet-mirror-crm-${index}`"
                >{{ row.crm || '—' }}</span
              >
              <DoctorSuggestionCell
                v-else-if="containerId"
                :model-value="row.crm"
                :container-id="containerId"
                field="crm"
                column-key="rawCrm"
                :row-index="index"
                @update:model-value="emitCellValue(index, 'crm', $event)"
                @select="selectSuggestion(index, $event)"
              />
              <input
                v-else
                type="text"
                class="w-full rounded border border-lightGray30 px-2 py-1 text-sm font-mono"
                placeholder="CRM"
                :value="row.crm"
                :data-testid="`sheet-mirror-crm-input-${index}`"
                @input="emitCell(index, 'crm', $event)"
              />
            </td>

            <template v-for="field in TIME_FIELDS" :key="field.key">
              <td class="py-1.5 px-2 align-middle">
                <input
                  type="time"
                  class="w-full rounded border px-2 py-1 text-sm font-mono"
                  :class="timeInputClass(row, field.key)"
                  :value="row[field.key]"
                  :disabled="disabled || isAbsent(row)"
                  :aria-label="`${field.label} da linha ${index + 1}`"
                  :data-testid="`sheet-mirror-${field.key}-${index}`"
                  @change="emitCell(index, field.key, $event)"
                />
                <!-- Valor anterior riscado: só conferência visual, o veredito vem do chip. -->
                <span
                  v-if="showPrevious(row, field.key)"
                  class="block mt-0.5 text-[10px] text-neutral50 font-mono"
                  :data-testid="`sheet-mirror-${field.key}-previous-${index}`"
                >
                  antes: <s>{{ row[field.previousKey] }}</s>
                </span>
              </td>
              <!--
                Conferência de assinatura/carimbo: nasce marcada, o operador desmarca o que
                faltar olhando a folha física. Caixa desmarcada numa linha em uso ganha destaque
                — é o que trava o "Avançar para auditoria" (a tela decide o gate, aqui só se
                mostra).
              -->
              <td v-if="showSignatureStamp" class="py-1.5 px-2 align-middle text-center">
                <input
                  type="checkbox"
                  class="h-4 w-4 rounded align-middle"
                  :class="signatureStampClass(row, field.checkKey)"
                  :checked="row[field.checkKey]"
                  :disabled="disabled || isAbsent(row)"
                  :aria-label="`${field.checkLabel} da linha ${index + 1}`"
                  :data-testid="`sheet-mirror-${field.checkKey}-${index}`"
                  @change="emitCheck(index, field.checkKey, $event)"
                />
              </td>
            </template>

            <td class="py-1.5 px-2 align-middle text-center whitespace-nowrap">
              <!--
                O travessão desta célula não dizia o que fazer a respeito. O tooltip agora
                aponta para o ⌖ ao lado, que é como a página se marca — não há OCR aqui.
              -->
              <v-tooltip :text="pageCellTitle(row)" location="top">
                <template #activator="{ props: tooltipProps }">
                  <button
                    v-bind="tooltipProps"
                    type="button"
                    class="h-6 min-w-6 rounded px-1.5 text-xs font-mono font-bold leading-none"
                    :class="
                      pageOf(row) === null
                        ? 'text-neutral50'
                        : 'text-primary30 underline hover:bg-primary5'
                    "
                    :disabled="pageOf(row) === null"
                    :title="pageCellTitle(row)"
                    :aria-label="
                      pageOf(row) === null
                        ? `Linha ${index + 1} sem página marcada`
                        : `Ir para a página ${pageOf(row)} da folha`
                    "
                    :data-testid="`sheet-mirror-page-${index}`"
                    @click.stop="jumpToRowPage(row)"
                  >
                    {{ formatPageCell(row.pageNumber) }}
                  </button>
                </template>
              </v-tooltip>
              <button
                type="button"
                class="ml-1 h-6 w-6 rounded text-[13px] leading-none text-neutral50 hover:bg-primary5 disabled:opacity-40"
                :disabled="disabled || !isValidPage(currentPage) || pageOf(row) === currentPage"
                :title="markCurrentPageTitle"
                :aria-label="`Marcar a página atual na linha ${index + 1}`"
                :data-testid="`sheet-mirror-mark-page-${index}`"
                @click.stop="emit('markCurrentPage', index)"
              >
                ⌖
              </button>
            </td>

            <td class="py-1.5 px-2 align-middle text-center relative">
              <!--
                O ⋯ guarda apenas o que sobrou depois que as ações principais viraram ícones:
                "Desfazer conciliação" e "Remover linha". Sem nenhum item ele não é renderizado
                — um menu que abre vazio é pior que nenhum menu —, e um espaço da mesma largura
                mantém a coluna alinhada.
              -->
              <button
                v-if="hasRowMenu(row)"
                type="button"
                class="h-6 w-6 rounded text-neutral50 font-bold leading-none hover:bg-primary5"
                :aria-label="`Mais ações da linha ${index + 1}`"
                :aria-expanded="openMenuIndex === index"
                :data-testid="`sheet-mirror-menu-button-${index}`"
                :disabled="disabled"
                @click.stop="toggleMenu(index, $event)"
              >
                ⋯
              </button>
              <span v-else class="inline-block h-6 w-6" aria-hidden="true" />

              <div
                v-if="openMenuIndex === index"
                class="sheet-mirror-popover"
                role="menu"
                :style="menuStyle"
                :data-testid="`sheet-mirror-menu-${index}`"
              >
                <button
                  v-if="isAutomaticallyConciliated(row)"
                  type="button"
                  role="menuitem"
                  class="sheet-mirror-menu-item"
                  :data-testid="`sheet-mirror-undo-conciliation-${row.entryId}`"
                  @click.stop="undoConciliation(index)"
                >
                  Desfazer conciliação
                </button>
                <!--
                  Linha extra ainda não enviada: descartar é local e sem rastro, porque
                  ela não existe no backend. Uma extra JÁ conciliada não entra aqui — sair
                  do banco exigiria endpoint de exclusão, e apagar só na tela faria a linha
                  reaparecer no reload.
                -->
                <button
                  v-if="isDiscardableExtra(row)"
                  type="button"
                  role="menuitem"
                  class="sheet-mirror-menu-item text-negative"
                  :data-testid="`sheet-mirror-remove-row-${index}`"
                  @click.stop="removeRow(index)"
                >
                  Remover linha
                </button>
              </div>

              <!-- Substituir médico: busca de candidatos da base, jamais texto livre. -->
              <div
                v-if="replaceIndex === index"
                class="sheet-mirror-popover sheet-mirror-popover-wide"
                :style="replaceStyle"
                :data-testid="`sheet-mirror-replace-popover-${index}`"
              >
                <p class="px-3 pt-2 text-xs font-bold text-textPrimary">
                  Substituir médico — linha {{ index + 1 }}
                </p>
                <p class="px-3 text-[10px] text-neutral50">{{ row.doctorName }} · {{ row.crm }}</p>
                <div class="px-3 py-2">
                  <input
                    v-model="replaceSearch"
                    type="text"
                    class="w-full rounded border border-primary30 px-2 py-1 text-sm"
                    placeholder="Buscar por nome ou CRM"
                    :data-testid="`sheet-mirror-replace-search-${index}`"
                  />
                </div>
                <ul class="max-h-56 overflow-y-auto px-2 pb-1">
                  <li v-for="candidate in replaceCandidates" :key="candidate.doctorStationId">
                    <button
                      type="button"
                      class="w-full flex items-center gap-2 rounded border border-lightGray30 px-2 py-1.5 mb-1 text-left hover:bg-primary5"
                      :data-testid="`sheet-mirror-candidate-${candidate.doctorStationId}`"
                      @click.stop="confirmReplace(index, candidate.doctorStationId)"
                    >
                      <span class="min-w-0 flex-1">
                        <span class="block text-xs font-bold truncate">{{ candidate.name }}</span>
                        <span class="block text-[10px] text-neutral50 font-mono">{{
                          candidate.crm
                        }}</span>
                      </span>
                      <span
                        v-if="candidate.isSuggested"
                        class="rounded-full border border-sucess px-2 py-0.5 text-[9px] font-bold text-sucess"
                        :data-testid="`sheet-mirror-candidate-suggested-${candidate.doctorStationId}`"
                        >Sugerido</span
                      >
                    </button>
                  </li>
                  <li
                    v-if="replaceCandidates.length === 0"
                    class="px-2 py-2 text-[11px] text-neutral50"
                    data-testid="sheet-mirror-candidates-empty"
                  >
                    Nenhum profissional escalado neste turno corresponde à busca.
                  </li>
                </ul>
                <p class="px-3 pb-2 text-[10px] text-neutral50">
                  A escala oficial não muda: a substituição vale apenas para esta linha da folha.
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-2 flex flex-wrap items-center gap-2">
      <OutlineButton
        text="Adicionar linha"
        width="150px"
        height="36px"
        data-testid="sheet-mirror-add-row"
        :disabled="disabled || !canAddRow"
        @click="emit('addRow')"
      />
      <div class="ml-auto flex flex-wrap items-center gap-2 text-[10px]">
        <span
          v-for="entry in legend"
          :key="entry.label"
          class="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-bold"
          :class="entry.chipClass"
          :data-testid="`sheet-mirror-legend-${entry.key}`"
        >
          {{ entry.count }} {{ entry.label }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * ESP-02/29-04 — a grade do "Espelho da Escala" (mockup 05, aprovado pelo usuário em
 * 03/09). Não é a grade de planilha (`AttendanceSheetCard`): aqui a linha não nasce vazia,
 * ela nasce da escala prevista, e o operador só toca no que a folha física contradiz.
 *
 * O componente é puramente apresentacional — decide zero sobre o domínio. Todo estado de
 * linha, contagem e candidato vem de `utils/prefill-mirror.ts`, e toda ação sobe por evento
 * para a tela, que é quem fala com a API.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { mdiAccountOffOutline, mdiAccountSwitchOutline, mdiUndoVariant } from '@mdi/js'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import DoctorSuggestionCell from './DoctorSuggestionCell.vue'
import { formatCrm } from '../utils/crm-format'
import type { DSAttendanceDoctorSuggestion } from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import { PrefillOutcome, PREFILL_OUTCOME_CHIP_LABELS } from '@/constants/ds-attendance.constants'
import {
  buildReplacementCandidates,
  isAutomaticallyConciliated,
  prefillCoverage,
  prefillCoverageLabel,
  resolveRowState,
  rowStateChipLabel,
  tallyOutcomes,
  type MirrorRow
} from '../utils/prefill-mirror'
import { formatPageCell, isValidPage, resolveRowPage } from '../utils/sheet-page-map'

const props = defineProps<{
  rows: MirrorRow[]
  /** Requisição em voo (prefill, conferência, ausência): a grade fica somente leitura. */
  disabled?: boolean
  canAddRow?: boolean
  /**
   * Id do registro de plantão — único contexto que o autocomplete de médico
   * exige. Ausente, as linhas extra caem no input simples.
   */
  containerId?: string
  /**
   * Colunas de conferência de assinatura/carimbo (pacote 2026-09-10). A tela desliga na
   * modalidade app externo, como a grade clássica faz com as colunas equivalentes.
   */
  showSignatureStamp?: boolean
  /**
   * Cara-crachá V1 — página que o visualizador mostra agora, para o botão "marcar página
   * atual" saber o que gravar e ficar desabilitado quando não há o que marcar.
   */
  currentPage?: number | null
  /** Índice da linha selecionada — realce `sel` do mockup V1. */
  selectedIndex?: number | null
}>()

const emit = defineEmits<{
  /** Edição de célula: a tela aplica o patch e recalcula o estado da linha. */
  updateCell: [payload: { index: number; field: keyof MirrorRow; value: string | boolean }]
  /**
   * Sugestão escolhida no autocomplete: nome E CRM num patch só, para a linha
   * não re-renderizar duas vezes com identidade meio preenchida.
   */
  selectDoctor: [payload: { index: number; doctorName: string; crm: string }]
  addRow: []
  /** Descarte de linha extra ainda não enviada — ver `isDiscardableExtra`. */
  removeRow: [index: number]
  markAbsent: [index: number]
  /** Desfaz a ausência — a linha volta ao estado de pós-espelho. */
  unmarkAbsent: [index: number]
  replaceDoctor: [payload: { index: number; doctorStationId: number }]
  /** BUG UI-8 — "Desfazer conciliação": a tela chama o mesmo endpoint do fluxo clássico. */
  undoConciliation: [entryId: number]
  /** Cara-crachá V1 — pular o visualizador para a página marcada nesta linha. */
  jumpToPage: [page: number]
  /** Gravar na linha a página que o visualizador mostra agora. */
  markCurrentPage: [index: number]
  /** Clique na linha: a tela realça e sincroniza a folha. */
  selectRow: [index: number]
}>()

const TIME_FIELDS = [
  {
    key: 'checkin',
    previousKey: 'prefilledCheckin',
    label: 'Entrada',
    checkKey: 'hasDoctorSignature',
    checkLabel: 'Ass. e Carimbo Entrada'
  },
  {
    key: 'checkout',
    previousKey: 'prefilledCheckout',
    label: 'Saída',
    checkKey: 'hasDoctorStamp',
    checkLabel: 'Ass. e Carimbo Saída'
  }
] as const

/**
 * Textos das ações de linha. Eles dizem o que a ação faz E o que ela não é: "substituir" já
 * foi confundido com digitar outro nome, e "não consta" com "faltou ao plantão".
 */
const REPLACE_ACTION_TOOLTIP =
  'Substituir médico — trocar esta linha por outro médico da base (não é texto livre)'
const MARK_ABSENT_ACTION_TOOLTIP =
  'Não consta na folha — o médico previsto na escala não assinou esta folha'
const UNMARK_ABSENT_ACTION_TOOLTIP = 'Desfazer "não consta na folha"'

/** Aspas dentro do rótulo não cabem num atributo de template — daí a função. */
function unmarkAbsentAriaLabel(index: number): string {
  return `${UNMARK_ABSENT_ACTION_TOOLTIP} na linha ${index + 1}`
}

type TimeFieldKey = (typeof TIME_FIELDS)[number]['key']
type SignatureStampKey = (typeof TIME_FIELDS)[number]['checkKey']

const openMenuIndex = ref<number | null>(null)
const replaceIndex = ref<number | null>(null)
const replaceSearch = ref('')

/**
 * Retângulo do botão ⋯ que abriu o overlay, em coordenadas de viewport.
 *
 * Os popovers são `position: fixed` posicionados a partir daqui, e não
 * `absolute` dentro da célula: a grade vive num container `overflow-x-auto`
 * (necessário para a tabela larga em telas pequenas), e o CSS obriga
 * `overflow-y` a acompanhar o eixo x. Ancorado na célula, o popover era
 * recortado por esse container e mexia no scroll da grade em vez de flutuar
 * por cima. `fixed` não entra no fluxo nem é recortado por overflow de
 * ancestral — a tela é uma rota, sem modal/`transform` no caminho que pudesse
 * criar um containing block.
 */
const anchorRect = ref<DOMRect | null>(null)

const POPOVER_GAP = 4
const VIEWPORT_MARGIN = 8
/** Largura mínima das duas variantes (ver `.sheet-mirror-popover*` no style). */
const MENU_WIDTH = 190
const REPLACE_WIDTH = 320
/**
 * Alturas APROXIMADAS, usadas só para decidir se o popover abre para baixo ou
 * para cima. Errar por alguns pixels desloca a abertura; não recorta nada.
 */
const MENU_HEIGHT_ESTIMATE = 140
const REPLACE_HEIGHT_ESTIMATE = 340

function buildPopoverStyle(width: number, heightEstimate: number) {
  const rect = anchorRect.value
  if (!rect) return undefined

  /*
   * O popover cresce PARA DENTRO da tela: gatilho na metade esquerda alinha pela borda
   * esquerda, na metade direita pela direita. Alinhar sempre pela direita vinha do ⋯, que
   * mora na ponta da tabela; com "Substituir médico" promovido a ícone ao lado do nome, os
   * 320 px do popover eram empurrados contra a margem esquerda, longe da linha trocada.
   * O clamp final garante que ele não vaze por nenhum dos lados.
   */
  const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN)
  const anchorsLeft = rect.left + rect.width / 2 < window.innerWidth / 2
  const preferredLeft = anchorsLeft ? rect.left : rect.right - width
  const left = Math.min(Math.max(VIEWPORT_MARGIN, preferredLeft), maxLeft)

  const fitsBelow = window.innerHeight - rect.bottom >= heightEstimate + VIEWPORT_MARGIN
  const vertical = fitsBelow
    ? { top: `${rect.bottom + POPOVER_GAP}px` }
    : { bottom: `${window.innerHeight - rect.top + POPOVER_GAP}px` }

  return { position: 'fixed' as const, left: `${left}px`, ...vertical }
}

const menuStyle = computed(() => buildPopoverStyle(MENU_WIDTH, MENU_HEIGHT_ESTIMATE))
const replaceStyle = computed(() => buildPopoverStyle(REPLACE_WIDTH, REPLACE_HEIGHT_ESTIMATE))

const coverage = computed(() => prefillCoverage(props.rows))
const coverageLabel = computed(() => prefillCoverageLabel(coverage.value))

const replaceCandidates = computed(() => {
  const index = replaceIndex.value
  if (index === null) return []
  return buildReplacementCandidates(props.rows, props.rows[index], replaceSearch.value)
})

const legend = computed(() => {
  const tally = tallyOutcomes(props.rows)
  return (
    [
      { key: PrefillOutcome.CONFIRMED, chipClass: 'border-lightGray30 text-neutral50' },
      { key: PrefillOutcome.EDITED, chipClass: 'border-alert text-alert' },
      { key: PrefillOutcome.ABSENT, chipClass: 'border-negative text-negative' },
      { key: PrefillOutcome.EXTRA, chipClass: 'border-primary10 text-primary30' }
    ] as const
  )
    .filter((entry) => tally[entry.key] > 0)
    .map((entry) => ({
      key: entry.key,
      chipClass: entry.chipClass,
      count: tally[entry.key],
      label: PREFILL_OUTCOME_CHIP_LABELS[entry.key].toLowerCase()
    }))
})

function isAbsent(row: MirrorRow): boolean {
  return resolveRowState(row) === PrefillOutcome.ABSENT
}

/**
 * `entryId === null` é a definição de linha extra recém-adicionada que ainda não
 * existe no backend (ver `MirrorRow.entryId`): removê-la é só descartar estado
 * local. Depois de enviada a linha tem id e passa a ser assunto do backend.
 */
function isDiscardableExtra(row: MirrorRow): boolean {
  return row.entryId === null
}

/**
 * Com "Substituir médico" e "Não consta na folha" promovidas a ícones, o ⋯ só faz sentido
 * quando ainda resta algo dentro dele.
 */
function hasRowMenu(row: MirrorRow): boolean {
  return isAutomaticallyConciliated(row) || isDiscardableExtra(row)
}

/** Realce da linha selecionada (`sel` do mockup V1) — anel, não troca de fundo. */
const SELECTED_ROW_CLASS = 'outline outline-2 -outline-offset-2 outline-primary30 bg-primary5/30'

const markCurrentPageTitle = computed(() =>
  isValidPage(props.currentPage)
    ? `Marcar esta linha como página ${props.currentPage} da folha`
    : 'Abra uma página da folha para poder marcá-la'
)

/** Página utilizável da linha — `null` quando ninguém marcou ainda. */
function pageOf(row: MirrorRow): number | null {
  return resolveRowPage(row)
}

/** Sem página, o texto tem de ensinar a saída: o ⌖ ao lado é quem marca. */
function pageCellTitle(row: MirrorRow): string {
  const page = pageOf(row)
  return page === null
    ? 'Linha sem página marcada — use ⌖ para marcar a página que o visualizador mostra'
    : `Ir para a página ${page} da folha`
}

function jumpToRowPage(row: MirrorRow): void {
  const page = pageOf(row)
  if (page !== null) emit('jumpToPage', page)
}

function rowClass(row: MirrorRow): string {
  const state = resolveRowState(row)
  // Ausente é um desfecho legítimo e reversível, não um erro: o chip vermelho
  // já comunica o estado, então a linha fica em cinza-claro em vez de vermelha.
  if (state === PrefillOutcome.ABSENT) return 'bg-lightGray30/40 border-b border-lightGray30'
  if (state === PrefillOutcome.EDITED) return 'bg-[#FDF1DF] border-b border-lightGray30'
  if (state === PrefillOutcome.EXTRA) return 'bg-primary5/40 border-b border-lightGray30'
  return 'border-b border-lightGray30'
}

function chipClass(row: MirrorRow): string {
  const state = resolveRowState(row)
  if (state === PrefillOutcome.ABSENT) return 'bg-white border-negative text-negative'
  if (state === PrefillOutcome.EDITED) return 'bg-white border-alert text-alert'
  if (state === PrefillOutcome.EXTRA) return 'bg-white border-primary10 text-primary30'
  if (state === PrefillOutcome.CONFIRMED) return 'bg-white border-sucess text-sucess'
  return 'bg-gray-100 border-lightGray30 text-neutral50'
}

function chipDotClass(row: MirrorRow): string {
  const state = resolveRowState(row)
  if (state === PrefillOutcome.ABSENT) return 'bg-negative'
  if (state === PrefillOutcome.EDITED) return 'bg-alert'
  if (state === PrefillOutcome.EXTRA) return 'bg-primary30'
  if (state === PrefillOutcome.CONFIRMED) return 'bg-sucess'
  return 'bg-neutralLight'
}

function timeInputClass(row: MirrorRow, field: TimeFieldKey): string {
  if (isAbsent(row)) return 'border-negative line-through text-negative bg-white'
  return showPrevious(row, field)
    ? 'border-alert font-bold text-alert bg-white'
    : 'border-lightGray30 bg-white'
}

/** O riscado só aparece quando o valor atual difere do que o espelho escreveu. */
function showPrevious(row: MirrorRow, field: TimeFieldKey): boolean {
  const previous = field === 'checkin' ? row.prefilledCheckin : row.prefilledCheckout
  return Boolean(row.isPrefillBorn && previous) && row[field] !== previous
}

/**
 * Seleção de linha por clique, mas NÃO a partir de um campo: `selectRow` faz o visualizador
 * pular para a página da linha, e a folha saltar debaixo de quem acabou de clicar no input
 * para digitar é desorientador. Quem entra pelo teclado (`Tab`) continua selecionando, pelo
 * `@focusin` da linha — o realce tem de acompanhar o cursor.
 */
const INTERACTIVE_TAGS = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A', 'OPTION']

function handleRowClick(index: number, event: MouseEvent) {
  const target = event.target as HTMLElement | null
  if (target?.isContentEditable) return
  if (target?.tagName && INTERACTIVE_TAGS.includes(target.tagName)) return
  emit('selectRow', index)
}

function emitCell(index: number, field: keyof MirrorRow, event: Event) {
  const value = (event.target as HTMLInputElement).value
  emit('updateCell', { index, field, value })
}

function emitCheck(index: number, field: SignatureStampKey, event: Event) {
  emit('updateCell', { index, field, value: (event.target as HTMLInputElement).checked })
}

/** Caixa desmarcada numa linha em uso é o que trava o avanço: destaque de atenção. */
function signatureStampClass(row: MirrorRow, field: SignatureStampKey): string {
  if (isAbsent(row)) return 'opacity-40'
  return row[field] ? 'accent-primary30' : 'outline outline-2 outline-alert'
}

/** O `DoctorSuggestionCell` já entrega o valor pronto, sem passar pelo evento. */
function emitCellValue(index: number, field: keyof MirrorRow, value: string) {
  emit('updateCell', { index, field, value })
}

function selectSuggestion(index: number, suggestion: DSAttendanceDoctorSuggestion) {
  emit('selectDoctor', {
    index,
    doctorName: suggestion.name,
    crm: formatCrm(suggestion)
  })
}

function closeOverlays() {
  openMenuIndex.value = null
  replaceIndex.value = null
  replaceSearch.value = ''
  anchorRect.value = null
}

function toggleMenu(index: number, event?: MouseEvent) {
  replaceIndex.value = null
  const willOpen = openMenuIndex.value !== index
  openMenuIndex.value = willOpen ? index : null

  const trigger = event?.currentTarget as HTMLElement | undefined
  anchorRect.value = willOpen && trigger ? trigger.getBoundingClientRect() : null
}

/**
 * O popover de substituição é `fixed` e se posiciona a partir de `anchorRect`. Agora que ele
 * abre pelo ícone da linha (e não mais pelo ⋯), é esse botão que precisa ancorá-lo — sem o
 * retângulo o popover voltaria a nascer no canto, longe da linha que está sendo trocada.
 */
function openReplace(index: number, event?: MouseEvent) {
  openMenuIndex.value = null
  replaceSearch.value = ''
  replaceIndex.value = index

  const trigger = event?.currentTarget as HTMLElement | undefined
  if (trigger) anchorRect.value = trigger.getBoundingClientRect()
}

function markAbsent(index: number) {
  openMenuIndex.value = null
  emit('markAbsent', index)
}

function unmarkAbsent(index: number) {
  closeOverlays()
  emit('unmarkAbsent', index)
}

function removeRow(index: number) {
  closeOverlays()
  emit('removeRow', index)
}

/** BUG UI-8 — ecoa o `entryId` da linha, o mesmo que `confirm-conciliation` já espera. */
function undoConciliation(index: number) {
  const row = props.rows[index]
  if (row.entryId === null) return
  openMenuIndex.value = null
  emit('undoConciliation', row.entryId)
}

function confirmReplace(index: number, doctorStationId: number) {
  emit('replaceDoctor', { index, doctorStationId })
  closeOverlays()
}

/**
 * Fechar por clique-fora escuta `mousedown`, que chega ANTES do `click` — e `@click.stop`
 * nos itens não segura `mousedown` nenhum. Sem esta guarda o popover fechava no aperto do
 * botão e o `click` do item nunca era entregue: "Substituir médico" e "Não consta na folha"
 * ficavam inertes ao mouse real (só respondiam a `click` sintético, o que fez os testes
 * antigos passarem). Um mousedown nascido DENTRO de um popover não é clique-fora.
 */
function handleOutsideClick(event: MouseEvent) {
  const target = event.target as Element | null
  if (target?.closest?.('.sheet-mirror-popover')) return
  closeOverlays()
}

function handleEscKey(event: KeyboardEvent) {
  if (event.key === 'Escape') closeOverlays()
}

const hasOverlay = computed(() => openMenuIndex.value !== null || replaceIndex.value !== null)

/**
 * Um popover `fixed` fica preso à viewport e não acompanha a linha que o
 * ancorou. Rolar ou redimensionar fecha — comportamento padrão de menu curto e
 * honesto com o usuário, em vez de deixar o popover órfão longe da linha.
 * `capture: true` para pegar também o scroll do container da grade, que não
 * borbulha até o `document`.
 */
function detachOverlayListeners() {
  document.removeEventListener('mousedown', handleOutsideClick)
  document.removeEventListener('keydown', handleEscKey)
  document.removeEventListener('scroll', closeOverlays, true)
  window.removeEventListener('resize', closeOverlays)
}

watch(hasOverlay, (open) => {
  if (open) {
    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscKey)
    document.addEventListener('scroll', closeOverlays, true)
    window.addEventListener('resize', closeOverlays)
  } else {
    detachOverlayListeners()
  }
})

onBeforeUnmount(detachOverlayListeners)
</script>

<style scoped>
/* Mesmos tokens do popover de menu de coluna do AttendanceSheetCard — sem token novo. */
/* `position`/`top`/`left` vêm inline de `buildPopoverStyle` (fixed, ancorado no
   botão ⋯) — a classe cuida só da aparência. */
.sheet-mirror-popover {
  position: fixed;
  z-index: 60;
  min-width: 190px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
  padding: 4px 0;
  text-align: left;
}

.sheet-mirror-popover-wide {
  min-width: 320px;
}

.sheet-mirror-menu-item {
  display: block;
  width: 100%;
  padding: 6px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: left;
  color: #323232;
}

.sheet-mirror-menu-item:hover:not(:disabled) {
  background: #d8eef3;
}

.sheet-mirror-menu-item:disabled {
  opacity: 0.5;
}
</style>
